import React, { useState, useEffect, useRef } from 'react';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  MessageCircle, 
  Send, 
  Plus, 
  Trash2, 
  User, 
  Bot,
  Loader2,
  Heart,
  AlertCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import axios from 'axios';
import { API } from '@/App';

const Chatbot = ({ user, setUser }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    loadChatSessions();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatSessions = async () => {
    try {
      const response = await axios.get(`${API}/chat/sessions`);
      setSessions(response.data);
      
      // If there are sessions and no current session, select the first one
      if (response.data.length > 0 && !currentSession) {
        setCurrentSession(response.data[0]);
        loadMessages(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading chat sessions:', error);
      toast({
        title: "Error",
        description: "Failed to load chat sessions",
        variant: "destructive"
      });
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadMessages = async (sessionId) => {
    if (!sessionId) return;
    
    setMessagesLoading(true);
    try {
      const response = await axios.get(`${API}/chat/sessions/${sessionId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast({
        title: "Error",
        description: "Failed to load messages",
        variant: "destructive"
      });
    } finally {
      setMessagesLoading(false);
    }
  };

  const createNewSession = async () => {
    try {
      const response = await axios.post(`${API}/chat/sessions`);
      const newSession = response.data;
      setSessions([newSession, ...sessions]);
      setCurrentSession(newSession);
      setMessages([]);
    } catch (error) {
      console.error('Error creating session:', error);
      toast({
        title: "Error",
        description: "Failed to create new chat session",
        variant: "destructive"
      });
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/chat/sessions/${sessionId}`);
      setSessions(sessions.filter(s => s.id !== sessionId));
      
      // If we deleted the current session, clear it
      if (currentSession?.id === sessionId) {
        setCurrentSession(null);
        setMessages([]);
        
        // Select another session if available
        const remainingSessions = sessions.filter(s => s.id !== sessionId);
        if (remainingSessions.length > 0) {
          setCurrentSession(remainingSessions[0]);
          loadMessages(remainingSessions[0].id);
        }
      }
      
      toast({
        title: "Success",
        description: "Chat session deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        title: "Error",
        description: "Failed to delete chat session",
        variant: "destructive"
      });
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentSession || loading) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setLoading(true);

    // Add user message to UI immediately
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await axios.post(`${API}/chat/sessions/${currentSession.id}/messages`, {
        content: messageText
      });

      // Add AI response to messages
      setMessages(prev => [...prev, response.data]);
      
      // Reload sessions to update message count and timestamp
      loadChatSessions();
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
      
      // Remove the user message on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const selectSession = (session) => {
    setCurrentSession(session);
    loadMessages(session.id);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    let greeting = "Good evening";
    if (hour < 12) greeting = "Good morning";
    else if (hour < 17) greeting = "Good afternoon";

    return `${greeting}, ${user?.full_name}! I'm your AI Health Assistant. I can help you with health questions, interpret your symptoms, provide wellness tips, and guide you through your healthcare journey. How can I assist you today?`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 sm:py-8 pt-24 ">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-teal-600" />
            AI Health Assistant
          </h1>
          <p className="text-gray-600">
            Get instant health guidance from your personal AI assistant
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-200px)]">
          {/* Chat Sessions Sidebar */}
          <div className="lg:col-span-1">
            <Card className="h-full flex flex-col">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Chat History</CardTitle>
                  <Button onClick={createNewSession} size="sm" className="bg-teal-600 hover:bg-teal-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex-1 p-0">
                <ScrollArea className="h-full">
                  {sessionsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                    </div>
                  ) : sessions.length === 0 ? (
                    <div className="text-center py-8 px-4">
                      <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500 text-sm">No chat sessions yet</p>
                      <p className="text-gray-400 text-xs mt-1">Start a new conversation</p>
                    </div>
                  ) : (
                    <div className="space-y-1 p-4">
                      {sessions.map((session) => (
                        <div
                          key={session.id}
                          className={`group p-3 rounded-lg cursor-pointer transition-colors ${
                            currentSession?.id === session.id
                              ? 'bg-teal-50 border-teal-200 border'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => selectSession(session)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-gray-800 truncate">
                                {session.title}
                              </h3>
                              <p className="text-xs text-gray-500 mt-1">
                                {session.message_count} messages
                              </p>
                              <p className="text-xs text-gray-400">
                                {new Date(session.updated_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-auto"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSession(session.id);
                              }}
                            >
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Chat Interface */}
          <div className="lg:col-span-3">
            <Card className="h-full flex flex-col">
              {currentSession ? (
                <>
                  <CardHeader className="border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{currentSession.title}</CardTitle>
                        <p className="text-sm text-gray-500 mt-1">
                          AI Health Assistant • Always available
                        </p>
                      </div>
                      <div className="flex items-center text-green-600">
                        <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse mr-2"></div>
                        <span className="text-sm font-medium">Online</span>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col p-0">
                    <ScrollArea className="flex-1 p-4">
                      {messagesLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="text-center py-12">
                          <Heart className="w-16 h-16 text-teal-600 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            Welcome to your AI Health Assistant!
                          </h3>
                          <div className="bg-teal-50 rounded-lg p-4 max-w-2xl mx-auto">
                            <p className="text-gray-700 text-sm leading-relaxed">
                              {getWelcomeMessage()}
                            </p>
                          </div>
                          <div className="mt-6 space-y-2 text-sm text-gray-600">
                            <p>💡 Ask about symptoms, health concerns, or wellness tips</p>
                            <p>📊 Get insights about your health data and reports</p>
                            <p>🔍 Discuss medical history and treatment options</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {messages.map((message) => (
                            <div
                              key={message.id}
                              className={`flex gap-3 ${
                                message.role === 'user' ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              {message.role === 'assistant' && (
                                <Avatar className="w-8 h-8 bg-teal-100">
                                  <AvatarFallback>
                                    <Bot className="w-4 h-4 text-teal-600" />
                                  </AvatarFallback>
                                </Avatar>
                              )}
                              
                              <div
                                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                                  message.role === 'user'
                                    ? 'bg-teal-600 text-white'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                <p
                                  className={`text-xs mt-1 ${
                                    message.role === 'user' ? 'text-teal-200' : 'text-gray-500'
                                  }`}
                                >
                                  {formatTimestamp(message.timestamp)}
                                </p>
                              </div>
                              
                              {message.role === 'user' && (
                                <Avatar className="w-8 h-8 bg-gray-100">
                                  <AvatarFallback>
                                    <User className="w-4 h-4 text-gray-600" />
                                  </AvatarFallback>
                                </Avatar>
                              )}
                            </div>
                          ))}
                          
                          {loading && (
                            <div className="flex gap-3 justify-start">
                              <Avatar className="w-8 h-8 bg-teal-100">
                                <AvatarFallback>
                                  <Bot className="w-4 h-4 text-teal-600" />
                                </AvatarFallback>
                              </Avatar>
                              <div className="bg-gray-100 rounded-lg px-4 py-2">
                                <div className="flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                                  <span className="text-sm text-gray-600">AI is thinking...</span>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </ScrollArea>

                    <Separator />

                    <div className="p-4">
                      <div className="flex gap-2">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Ask me about your health concerns..."
                          className="flex-1"
                          disabled={loading}
                        />
                        <Button
                          onClick={sendMessage}
                          disabled={!newMessage.trim() || loading}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                        <AlertCircle className="w-3 h-3" />
                        <span>
                          This AI provides general health information, not medical diagnosis. 
                          Consult healthcare professionals for serious concerns.
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">
                      Start a Conversation
                    </h3>
                    <p className="text-gray-500 mb-4 max-w-sm">
                      Create a new chat session to begin talking with your AI Health Assistant
                    </p>
                    <Button onClick={createNewSession} className="bg-teal-600 hover:bg-teal-700">
                      <Plus className="w-4 h-4 mr-2" />
                      New Chat
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
      
      <Toaster />
    </div>
  );
};

export default Chatbot;